import { useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { Box, Button, Layer, Text } from "grommet";
import { isUnclaimedProctor } from "../PodV2/sessionUtils";
import {
  applyQueueEvent,
  buildInitialQueue,
  isQueueRowClaimed,
  reconcileExistingQueue,
} from "../PodV2/podQueueList";
import {
  QueueClaimButton,
  QueueDataTable,
  waitingColumn,
} from "./QueueSessionList";

const urgentFlash = keyframes`
  0%,
  100% {
    background-color: #d32f2f;
    color: #ffffff;
    border-color: #d32f2f;
  }
  50% {
    background-color: #ffffff;
    color: #d32f2f;
    border-color: #d32f2f;
  }
`;

const FlashingUrgentButton = styled(Button)`
  && {
    animation: ${urgentFlash} 0.75s ease-in-out infinite;
    border: 2px solid #d32f2f;
    font-weight: 700;
  }

  && span {
    color: inherit;
  }

  @media (prefers-reduced-motion: reduce) {
    && {
      animation: none;
      background-color: #d32f2f;
      color: #ffffff;
    }
  }
`;

export function requestKey(item) {
  return `${item?.id}:${item?.generation}`;
}

export function mergeReadRequests(current = [], incoming = []) {
  const seen = new Set(current.map(requestKey));
  const next = [...current];
  incoming.forEach((item) => {
    const key = requestKey(item);
    if (!item?.id || seen.has(key)) return;
    seen.add(key);
    next.push(item);
  });
  return next;
}

export function commitQueueState(current, next) {
  return {
    ...current,
    sessions: next.sessions ?? current.sessions,
    claimedIds: next.claimedIds ?? current.claimedIds,
    pendingClaims: next.pendingClaims ?? current.pendingClaims ?? new Map(),
    claimVersions: next.claimVersions ?? current.claimVersions ?? new Map(),
    readRequests: mergeReadRequests(current.readRequests, next.readRequests),
    loaded: next.loaded ?? current.loaded,
  };
}

export function reducePodQueueState(current, event) {
  const next = applyQueueEvent(
    current.sessions ?? [],
    current.claimedIds ?? new Set(),
    event,
    current.pendingClaims ?? new Map(),
    current.claimVersions ?? new Map(),
  );
  return commitQueueState(current, next);
}

export function replayQueueEvents(current, pages, events = []) {
  const base = current.loaded
    ? reconcileExistingQueue(
        current.sessions,
        current.claimedIds,
        pages,
        events,
        current.pendingClaims,
        current.claimVersions,
      )
    : buildInitialQueue(pages, events);
  return commitQueueState(current, {
    ...base,
    loaded: true,
  });
}

export function urgentSessionsFromDisplayed(sessions = []) {
  return sessions.filter((session) => session?.isUrgent === true);
}

export function shouldShowUrgentPanel(sessions, claimedIds) {
  if (!sessions?.length) return false;
  return sessions.some(
    (session) =>
      isUnclaimedProctor(session?.proctor) &&
      !isQueueRowClaimed(claimedIds, session?.id),
  );
}

function useUrgentColumns({
  claimedIds,
  claimingId,
  onClaimSession,
  notTawkOrganizationIds,
}) {
  return useMemo(
    () => [
      {
        property: "actions",
        header: <Text>Actions</Text>,
        render: (datum) => (
          <QueueClaimButton
            session={datum}
            claimed={isQueueRowClaimed(claimedIds, datum.id)}
            claiming={claimingId === datum.id}
            notTawkOrganizationIds={notTawkOrganizationIds}
            onClaim={onClaimSession}
          />
        ),
        size: "small",
      },
      waitingColumn({ claimedIds }),
      {
        property: "testerName",
        header: <Text>Tester</Text>,
        render: (datum) => (
          <Text size="small" truncate>
            {datum.testerName || datum.testerEmail}
          </Text>
        ),
      },
      {
        property: "testerEmail",
        header: <Text>Email</Text>,
        render: (datum) => (
          <Text size="small" truncate>
            {datum.testerEmail}
          </Text>
        ),
      },
    ],
    [claimedIds, claimingId, onClaimSession, notTawkOrganizationIds],
  );
}

function UrgentQueueTable({
  sessions,
  claimedIds,
  claimingId,
  onClaimSession,
  notTawkOrganizationIds,
}) {
  const columns = useUrgentColumns({
    claimedIds,
    claimingId,
    onClaimSession,
    notTawkOrganizationIds,
  });
  return (
    <QueueDataTable
      sessions={sessions}
      claimedIds={claimedIds}
      columns={columns}
      notTawkOrganizationIds={notTawkOrganizationIds}
    />
  );
}

export function UrgentQueueButton({ onClick, style }) {
  return (
    <FlashingUrgentButton
      label="Urgent"
      onClick={onClick}
      className="urgent-queue-button"
      data-testid="urgent-button"
      pad={{ vertical: "0", horizontal: "12px" }}
      style={style}
    />
  );
}

export function UrgentQueuePopup({
  open,
  onClose,
  sessions,
  claimedIds,
  claimingId,
  notTawkOrganizationIds,
  onClaimSession,
}) {
  if (!open) return null;

  return (
    <Layer
      onEsc={onClose}
      onClickOutside={onClose}
      position="center"
      responsive
      animation={false}
    >
      <Box
        pad="medium"
        gap="small"
        width={{ max: "100vw" }}
        data-testid="urgent-popup"
      >
        <Box direction="row" justify="between" align="center">
          <Text weight="bold">Urgent</Text>
          <Button label="Close" onClick={onClose} />
        </Box>
        <UrgentQueueTable
          sessions={sessions}
          claimedIds={claimedIds}
          claimingId={claimingId}
          notTawkOrganizationIds={notTawkOrganizationIds}
          onClaimSession={onClaimSession}
        />
      </Box>
    </Layer>
  );
}

export function UrgentQueuePanel({
  sessions,
  claimedIds,
  claimingId,
  notTawkOrganizationIds,
  onClaimSession,
}) {
  if (!shouldShowUrgentPanel(sessions, claimedIds)) {
    return null;
  }

  return (
    <Box
      flex={{ grow: 0, shrink: 0 }}
      width={{ min: "220px", max: "300px" }}
      gap="small"
      data-testid="urgent-panel"
    >
      <Text weight="bold">Urgent</Text>
      <UrgentQueueTable
        sessions={sessions}
        claimedIds={claimedIds}
        claimingId={claimingId}
        notTawkOrganizationIds={notTawkOrganizationIds}
        onClaimSession={onClaimSession}
      />
    </Box>
  );
}
